import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  ArrowLeft, 
  CreditCard, 
  Lock, 
  CheckCircle, 
  Smartphone,
  Shield,
  Eye,
  EyeOff,
  Loader
} from 'lucide-react';
import { useApp } from '../context/useApp.js';
import toast from 'react-hot-toast';

const PaymentProcessor = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, language } = useApp();
  const booking = location.state?.booking;

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardData, setCardData] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: ''
  });
  const [upiData, setUpiData] = useState({
    upiId: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCvv, setShowCvv] = useState(false);
  const [errors, setErrors] = useState({});

  const translations = {
    en: {
      title: 'Payment',
      subtitle: 'Complete your booking payment',
      cardPayment: 'Credit/Debit Card',
      upiPayment: 'UPI',
      cardNumber: 'Card Number',
      cardName: 'Cardholder Name',
      expiryDate: 'Expiry Date',
      cvv: 'CVV',
      upiId: 'UPI ID',
      payAmount: 'Pay Amount',
      totalAmount: 'Total Amount',
      proceedToPay: 'Proceed to Pay',
      confirmPayment: 'Confirm Payment',
      cancel: 'Cancel',
      required: 'This field is required',
      paymentSuccess: 'Payment successful!',
      paymentError: 'Payment failed. Please try again.',
      processing: 'Processing payment...',
      securityNote: 'Your payment information is encrypted and secure'
    },
    hi: {
      title: 'भुगतान',
      subtitle: 'अपना बुकिंग भुगतान पूरा करें',
      cardPayment: 'क्रेडिट/डेबिट कार्ड',
      upiPayment: 'UPI',
      cardNumber: 'कार्ड नंबर',
      cardName: 'कार्डधारक का नाम',
      expiryDate: 'समाप्ति तिथि',
      cvv: 'CVV',
      upiId: 'UPI ID',
      payAmount: 'भुगतान राशि',
      totalAmount: 'कुल राशि',
      proceedToPay: 'भुगतान के लिए आगे बढ़ें',
      confirmPayment: 'भुगतान की पुष्टि करें',
      cancel: 'रद्द करें',
      required: 'यह फील्ड आवश्यक है',
      paymentSuccess: 'भुगतान सफल!',
      paymentError: 'भुगतान विफल। कृपया पुनः प्रयास करें।',
      processing: 'भुगतान प्रक्रिया में...',
      securityNote: 'आपकी भुगतान जानकारी एन्क्रिप्टेड और सुरक्षित है'
    }
  };

  const t = translations[language] || translations.en;

  const paymentMethods = [
    { id: 'card', name: t.cardPayment, icon: CreditCard },
    { id: 'upi', name: t.upiPayment, icon: Smartphone }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (paymentMethod === 'card') {
      if (!cardData.number) newErrors.cardNumber = t.required;
      if (!cardData.name) newErrors.cardName = t.required;
      if (!cardData.expiry) newErrors.expiry = t.required;
      if (!cardData.cvv) newErrors.cvv = t.required;
    } else {
      if (!upiData.upiId) newErrors.upiId = t.required;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    if (paymentMethod === 'card') {
      setCardData(prev => ({ ...prev, [field]: value }));
    } else {
      setUpiData(prev => ({ ...prev, [field]: value }));
    }

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const calculateTotal = () => {
    const baseAmount = booking?.amount || 0;
    const bookingFee = 100;
    const taxes = Math.round(baseAmount * 0.18);
    return baseAmount + bookingFee + taxes;
  };

  const handlePayment = async () => {
    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    setIsProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));

      const payment = {
        id: `TXN${Date.now()}`,
        bookingId: booking.id,
        amount: calculateTotal(),
        paymentMethod,
        status: 'success',
        timestamp: new Date().toISOString()
      };

      const existingPayments = JSON.parse(localStorage.getItem('payments') || '[]');
      existingPayments.push(payment);
      localStorage.setItem('payments', JSON.stringify(existingPayments));

      toast.success(t.paymentSuccess, {
        duration: 3000,
        position: 'top-center',
      });

      navigate('/bookings');
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(t.paymentError);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 dark:text-gray-400">No booking selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">
              {t.title}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t.subtitle}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Payment Form */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Payment Methods
                </h2>

                {/* Payment Method Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {paymentMethods.map(method => (
                    <button
                      key={method.id}
                      onClick={() => setPaymentMethod(method.id)}
                      className={`p-4 border rounded-lg text-left transition-colors ${
                        paymentMethod === method.id
                          ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <method.icon className="w-6 h-6 text-purple-600" />
                        <div className="font-medium text-gray-900 dark:text-white">
                          {method.name}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Payment Form */}
                <div className="mb-6">
                  {paymentMethod === 'card' ? (
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t.cardNumber} *
                        </label>
                        <input
                          type="text"
                          value={cardData.number}
                          onChange={(e) => handleInputChange('number', e.target.value)}
                          placeholder="1234 5678 9012 3456"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        {errors.cardNumber && (
                          <p className="text-red-500 text-sm mt-1">{errors.cardNumber}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          {t.cardName} *
                        </label>
                        <input
                          type="text"
                          value={cardData.name}
                          onChange={(e) => handleInputChange('name', e.target.value)}
                          placeholder="John Doe"
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        />
                        {errors.cardName && (
                          <p className="text-red-500 text-sm mt-1">{errors.cardName}</p>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t.expiryDate} *
                          </label>
                          <input
                            type="text"
                            value={cardData.expiry}
                            onChange={(e) => handleInputChange('expiry', e.target.value)}
                            placeholder="MM/YY"
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          />
                          {errors.expiry && (
                            <p className="text-red-500 text-sm mt-1">{errors.expiry}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {t.cvv} *
                          </label>
                          <div className="relative">
                            <input
                              type={showCvv ? 'text' : 'password'}
                              value={cardData.cvv}
                              onChange={(e) => handleInputChange('cvv', e.target.value)}
                              placeholder="123"
                              className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                            />
                            <button
                              type="button"
                              onClick={() => setShowCvv(!showCvv)}
                              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                              {showCvv ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                          </div>
                          {errors.cvv && (
                            <p className="text-red-500 text-sm mt-1">{errors.cvv}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t.upiId} *
                      </label>
                      <input
                        type="text"
                        value={upiData.upiId}
                        onChange={(e) => handleInputChange('upiId', e.target.value)}
                        placeholder="john@upi"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                      {errors.upiId && (
                        <p className="text-red-500 text-sm mt-1">{errors.upiId}</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Security Note */}
                <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg mb-6">
                  <Shield className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-700 dark:text-green-400">
                    {t.securityNote}
                  </span>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate(-1)}
                    className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t.cancel}
                  </button>
                  <button
                    onClick={handlePayment}
                    disabled={isProcessing}
                    className="flex-1 bg-purple-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProcessing ? (
                      <div className="flex items-center justify-center gap-2">
                        <Loader className="w-4 h-4 animate-spin" />
                        {t.processing}
                      </div>
                    ) : (
                      `Pay ₹${calculateTotal()}`
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - Payment Info */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {t.payAmount}
                </h3>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Booking Amount</span>
                    <span className="text-gray-900 dark:text-white">₹{booking.amount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Booking Fee</span>
                    <span className="text-gray-900 dark:text-white">₹100</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Taxes</span>
                    <span className="text-gray-900 dark:text-white">₹{Math.round(booking.amount * 0.18)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between font-semibold">
                      <span className="text-gray-900 dark:text-white">{t.totalAmount}</span>
                      <span className="text-purple-600">₹{calculateTotal()}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <Lock className="w-4 h-4 text-gray-400" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">
                    Powered by Razorpay
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentProcessor;
